package top.wcpe.allincore.ui.web

import top.wcpe.allincore.ui.runtime.event.SessionHitRegistry
import top.wcpe.allincore.ui.runtime.render.layer.DrawTracePhase
import kotlin.js.Promise

internal data class ModelCompositionRect(val x: Float, val y: Float, val width: Float, val height: Float)

internal data class ModelCompositionRequest(
    val sessionId: String,
    val instanceKey: String,
    val generation: Int,
    val phase: DrawTracePhase,
    val sequence: Int,
    val rect: ModelCompositionRect,
    val clip: ModelCompositionRect?,
    val transform: FloatArray,
    val alpha: Float,
    val source: String,
    val texture: String?,
    val modelBase: String,
    val scale: Float,
    val rotationX: Float,
    val rotationY: Float,
    val interactive: Boolean,
    val backgroundColor: Int,
)

internal interface ModelTextureLease {
    val leaseId: String
    fun onTextureReady(callback: (Texture) -> Unit)
    fun dispose()
}

internal sealed interface ModelCompositionResult {
    data class Ready(val textureLease: ModelTextureLease) : ModelCompositionResult
    data class Placeholder(val code: String) : ModelCompositionResult
    data object Cancelled : ModelCompositionResult
}

internal fun interface ModelCompositionHost {
    fun compose(request: ModelCompositionRequest): Promise<ModelCompositionResult>
    fun dispose() = Unit

    /** FR-UI-066：绑定当前 session 的命中注册表；null 表示解绑并清 hit。 */
    fun bindHitRegistry(registry: SessionHitRegistry<WebInputOwnerHandler>?) = Unit
}

internal class PlaceholderModelCompositionHost(
    private val sessionId: String,
    private val generation: () -> Int,
    private val disposed: () -> Boolean,
    private val diagnostics: WebRenderDiagnosticSink,
) : ModelCompositionHost {

    private var hostDisposed = false

    override fun compose(request: ModelCompositionRequest): Promise<ModelCompositionResult> {
        if (hostDisposed || disposed() || request.sessionId != sessionId || request.generation != generation()) {
            return Promise.resolve(ModelCompositionResult.Cancelled)
        }
        diagnostics.emit(
            WebRenderDiagnostic(
                PLACEHOLDER_CODE,
                WebRenderDiagnosticSeverity.INFO,
                "Web 模型合成宿主尚未接入 Three 实现，使用稳定占位",
                sessionId,
                generation(),
                MODEL_CAPABILITY_KEY,
                canonicalWebResourceKey(request.source),
                request.phase,
                request.sequence,
            ),
        )
        return Promise.resolve(ModelCompositionResult.Placeholder(PLACEHOLDER_CODE))
    }

    override fun dispose() {
        hostDisposed = true
    }

    private companion object {
        const val PLACEHOLDER_CODE = "WEB_RENDER_MODEL_PLACEHOLDER"
        const val MODEL_CAPABILITY_KEY = WebRenderDiagnosticCapabilityKeys.MODEL_COMPOSITION
    }
}
